<script setup>
import { ref, computed, watch } from 'vue';
import Draggable from 'vuedraggable';
import KanbanLeadCard from './KanbanLeadCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  leads: { type: Array, default: () => [] },
});

const emit = defineEmits(['cardClick', 'moveStage', 'addLead']);

// Local mutable copy so vuedraggable can mutate it freely.
// When Vuex state updates (after API success or failure), this resyncs automatically.
const localLeads = ref([...props.leads]);
watch(
  () => props.leads,
  val => {
    localLeads.value = [...val];
  }
);

const isDragOver = ref(false);
const stageColor = computed(() => props.stage.color || '#6366f1');

const totalBudget = computed(() => {
  const items = props.leads.filter(l => l.budget_max || l.budget_min);
  if (!items.length) return null;
  const total = items.reduce(
    (sum, l) => sum + Number(l.budget_max || l.budget_min || 0),
    0
  );
  const sym = items[0].currency === 'INR' ? '₹' : '';
  if (total >= 10000000) return `${sym}${(total / 10000000).toFixed(1)}Cr`;
  if (total >= 100000) return `${sym}${(total / 100000).toFixed(0)}L`;
  return `${sym}${total.toLocaleString()}`;
});

function onDragEnd(event) {
  // Only act on cross-column moves
  if (event.from === event.to) return;

  const leadId = Number(event.item?.dataset?.leadId);
  const targetStageKey = event.to?.dataset?.stageKey;

  if (!leadId || !targetStageKey) return;

  emit('moveStage', { leadId, stageKey: targetStageKey });
}

function onDragEnter() {
  isDragOver.value = true;
}
function onDragLeave() {
  isDragOver.value = false;
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="flex flex-col w-64 shrink-0 rounded-xl h-full overflow-hidden border transition-colors duration-150"
    :class="
      isDragOver
        ? 'border-woot-400 dark:border-woot-500 bg-woot-50/40 dark:bg-woot-500/5'
        : 'border-n-weak dark:border-n-strong bg-n-alpha-1 dark:bg-n-solid-2'
    "
  >
    <!-- Colored top accent -->
    <div
      class="h-1 w-full shrink-0 rounded-t-xl"
      :style="{ backgroundColor: stageColor }"
    />

    <!-- Column header -->
    <div
      class="flex items-center justify-between px-3 py-2 shrink-0 bg-n-background dark:bg-n-solid-2 border-b border-n-weak dark:border-n-strong"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="w-2 h-2 rounded-full shrink-0"
          :style="{ backgroundColor: stageColor }"
        />
        <span class="font-medium text-sm text-n-slate-12 truncate">{{
          stage.name
        }}</span>
        <span
          class="text-xs bg-n-alpha-2 dark:bg-n-solid-3 text-n-slate-9 font-semibold px-1.5 py-0.5 rounded-full shrink-0 min-w-[20px] text-center"
        >
          {{ localLeads.length }}
        </span>
      </div>
      <button
        class="flex items-center justify-center w-6 h-6 rounded-md text-n-slate-7 hover:text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors shrink-0"
        title="Add lead"
        @click="$emit('addLead', stage)"
      >
        <span class="i-lucide-plus size-3.5" />
      </button>
    </div>

    <!-- Budget total -->
    <div
      v-if="totalBudget"
      class="px-3 py-1 text-[11px] text-n-slate-8 font-medium bg-n-background dark:bg-n-solid-2 border-b border-n-weak dark:border-n-strong"
    >
      <span class="text-n-slate-7">Total:</span> {{ totalBudget }}
    </div>

    <!-- Draggable card list -->
    <Draggable
      v-model="localLeads"
      :data-stage-key="stage.key"
      :data-stage-id="stage.id"
      group="kanban-leads"
      item-key="id"
      animation="200"
      ghost-class="kanban-ghost"
      chosen-class="kanban-chosen"
      drag-class="kanban-dragging"
      handle=".drag-handle"
      class="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60px]"
      @end="onDragEnd"
      @dragenter="onDragEnter"
      @dragleave="onDragLeave"
      @dragover.prevent
    >
      <template #item="{ element }">
        <KanbanLeadCard :lead="element" @click="$emit('cardClick', element)" />
      </template>

      <template #footer>
        <div
          v-if="localLeads.length === 0"
          class="flex flex-col items-center justify-center py-8 gap-2 pointer-events-none"
        >
          <span class="i-lucide-inbox size-5 text-n-slate-5" />
          <p class="text-xs text-n-slate-9 text-center font-medium">No leads</p>
          <p class="text-xs text-n-slate-7 text-center">
            Drag a card here or create a new lead
          </p>
        </div>
      </template>
    </Draggable>

    <!-- Add lead footer -->
    <button
      class="flex items-center justify-center gap-1.5 text-xs text-n-slate-7 hover:text-woot-500 px-3 py-2 border-t border-n-weak dark:border-n-strong transition-colors shrink-0 w-full bg-n-background dark:bg-n-solid-2 hover:bg-woot-50 dark:hover:bg-woot-500/10"
      @click="$emit('addLead', stage)"
    >
      <span class="i-lucide-plus size-3.5" />
      Add lead
    </button>
  </div>
</template>

<style scoped>
/* Ghost: the placeholder shown in source column while dragging */
:deep(.kanban-ghost) {
  opacity: 0.35;
  border: 2px dashed theme('colors.woot.400');
  border-radius: 0.5rem;
  background: theme('colors.woot.50');
}

/* Chosen: the card being dragged */
:deep(.kanban-chosen) {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

/* Flying clone */
:deep(.kanban-dragging) {
  opacity: 0.95;
  transform: rotate(1.5deg);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}
</style>
